"use client"

import React, { useEffect, useRef, useState } from 'react'
import styles from './styles.module.scss'
import { useMerchant } from '@/src/context/merchant/merchant-context'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import BurgerIcon from './burger-icon/burger-icon'
import ContentDrawer from './content-drawer/content-drawer'
import SvgSearch from '@/src/assets/svgs/collection/search.svg'
import SvgClose from '@/src/assets/svgs/collection/close.svg'
import { useCategory } from '@/src/context/category/category-context'
import Drawer from '@/src/components/helpers/overlays/drawer/drawer'
import { ItemService } from '@/src/services/catalog/item-service'
import LoadingFullscreen from '@/src/components/loadings/loading-fullscreen/loading-fullscreen'
import { useMessage } from '@/src/context/message/message-context'
import { HighlightText } from '@/src/helpers/highlight-text'
import ActionInput from '@/src/components/misc/action-elements/action-input/action-input'
import MerchantLogo from '@/src/components/merchant/logo/merchant-logo'
import { useAppAccessToken } from '@/src/context/app-access-token/app-access-token-context'
import { OrderService } from '@/src/services/sales/order-service'
import { useCustomer } from '@/src/context/customer/customer-context'
import { getUserToken } from '@/src/functions/user-token'
import OrderHistory from '@/src/interfaces/sales/order-history'
import OrderGet from '@/src/interfaces/sales/order-get'

export default function Header() {
    const { merchantSummary, localMode } = useMerchant()
    const { showMessage } = useMessage()
    const { categoriesBySearch, setCategoriesBySearch, setSearchText, searchText = '' } = useCategory()
    const pathname = usePathname()
    const { appAccessToken } = useAppAccessToken()
    const { customer, customerLoginStatus } = useCustomer()

    const [showMerchant, setShowMerchant] = useState(true)
    const [isDrawer, setIsDrawer] = useState(false)
    const [showSearchInput, setShowSearchInput] = useState<boolean>(false)
    const [canSearch, setCanSearch] = useState<boolean>(true)
    const [isSearching, setIsSearching] = useState<boolean>(false)
    const [lastOrder, setLastOrder] = useState<OrderHistory.OrderItem | undefined>(undefined)
    const [orderStatus, setOrderStatus] = useState<OrderGet.StatusResponse | undefined>(undefined)

    const isOnDeliveryPage = pathname.startsWith('/pedido/acompanhar')
    const hasFetchedOrder = useRef(false)

    const [statusHasError, setStatusHasError] = useState(false)

    if (pathname.startsWith('/internal-error') || pathname.startsWith('/page-not-found'))
        return <></>

    const searchProducts = async () => {
        try {
            const searchTextTrim = searchText ? searchText.trim() : ''

            if (searchTextTrim.length < 3)
                return showMessage('Digite pelo menos 3 caracteres.')

            HighlightText.removeHighlight('cards-list')
            setIsSearching(true)

            const response = await ItemService.getBySearchSummary(merchantSummary?.id!, searchTextTrim, !!localMode, appAccessToken)

            if (!response.success)
                throw Error(response.errorMessage)

            if (!response.model)
                setCategoriesBySearch({
                    categories: []
                })
            else
                setCategoriesBySearch(response?.model)
        }
        catch (e) {
            const error = e as Error
            showMessage(error.message, { type: 'danger', error: error })
        }
        finally {
            setIsSearching(false)
        }
    }

    const handleCloseClick = () => {
        setShowSearchInput(false)
        setSearchText("")
        setCategoriesBySearch(undefined)
        HighlightText.removeHighlight('cards-list')
    }

    const isOrderValid = (currentStatus?: OrderGet.OrderStatus) => {
        if (currentStatus === 'Delivered'
            || currentStatus === 'Canceled'
            || currentStatus === 'Expired'
            || currentStatus === 'NotPlaced')
            return false
        return true
    }

    const isExpiredStatusTime = (createdAt: string) => {
        const _createdAt: Date = new Date(createdAt)
        const now = new Date()

        const createdMs = _createdAt.getTime()
        const nowMs = now.getTime()

        const diffMs = nowMs - createdMs

        const threeHoursInMs = 3 * 60 * 60 * 1000

        const isExpiredTime = diffMs >= threeHoursInMs

        return isExpiredTime
    }

    const fetchOrder = async () => {
        try {
            let userAccessToken = await getUserToken()

            const params: OrderHistory.SearchParams = {
                customerId: customer?.customerId!,
                merchantId: merchantSummary?.id!,
                pageSize: 1,
                onlyActiveOrders: true
            }

            const response = await OrderService.getOrders(params, userAccessToken)

            if (!response.success)
                return console.error(response.errorMessage)

            if (!response.model || (response.model?.items && response.model.items.length < 1))
                return

            const order = response.model.items[0]
            const expired = isExpiredStatusTime(order.createdAt)

            if (!isOrderValid(order?.status) || order?.autoClosed || expired) {
                setLastOrder(undefined)
                return
            }

            setLastOrder(order)
        }
        catch (e) {
            const error = e as Error
            showMessage(error.message, { type: 'danger', error: error })
        }
    }

    const fetchOrderStatus = async () => {
        try {
            if (!lastOrder)
                return

            if (statusHasError)
                return console.error('Order status failed.')

            const accessToken = await getUserToken()

            const response = await OrderService.getStatus(lastOrder.id, accessToken)

            if (!response.success)
                return console.error(response.errorMessage)

            if (!response.model)
                return

            if (response.model?.statuses && response.model.statuses.length > 0) {
                const lastStatus = response.model.statuses.at(-1)

                const expired = isExpiredStatusTime(lastOrder.createdAt)

                if (!isOrderValid(lastStatus?.status) || expired) {
                    setLastOrder(undefined)
                    setOrderStatus(undefined)
                    return
                }
            }

            setOrderStatus(response.model)
        }
        catch (e) {
            const error = e as Error
            showMessage(error.message, { type: 'danger', error: error })
        }
    }

    useEffect(() => {
        if (typeof document !== 'undefined') {
            const head = document.getElementById("page-header")

            const observer = new IntersectionObserver(entries => {
                if (!entries[0].isIntersecting)
                    setShowMerchant(true)
                else
                    setShowMerchant(false)
            }, {
                threshold: 0
            })

            if (head) {
                observer.observe(head)
                return () => {
                    observer.unobserve(head)
                }
            }
        }
    }, [pathname])

    useEffect(() => {
        const isHome = pathname === '/'
        setCanSearch(isHome)
    }, [pathname])

    useEffect(() => {
        if (categoriesBySearch === undefined)
            handleCloseClick()
    }, [categoriesBySearch])

    useEffect(() => {
        const isLoggedIn = customer && customerLoginStatus === 'loggedIn'
        const canFetch = !lastOrder && merchantSummary && isLoggedIn && !isOnDeliveryPage && !hasFetchedOrder.current

        if (canFetch) {
            fetchOrder()
            hasFetchedOrder.current = true
        }

        if (customerLoginStatus !== 'loggedIn' || isOnDeliveryPage) {
            setLastOrder(undefined)
            hasFetchedOrder.current = false
        }
    }, [merchantSummary, customerLoginStatus, customer, pathname])

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined

        if (lastOrder) {
            fetchOrderStatus()

            interval = setInterval(() => {
                fetchOrderStatus()
            }, 60000)

            return () => clearInterval(interval)
        }
        else {
            if (interval)
                clearInterval(interval)
        }
    }, [lastOrder, statusHasError])

    return (
        <>
            {isSearching && <LoadingFullscreen />}
            {
                isDrawer &&
                <Drawer
                    isOpen={isDrawer}
                    onClose={() => setIsDrawer(false)}>
                    <ContentDrawer orderStatus={orderStatus} />
                </Drawer>
            }
            <header className={styles.container} id='header'>
                <div className={styles.subContainer}>
                    <div className={styles.merchantInfo}>
                        {!localMode &&
                            <div className={styles.menuIcon}>
                                <BurgerIcon
                                    isOrderActive={!!lastOrder}
                                    onClick={() => setIsDrawer(!isDrawer)}
                                    isModalOpen={isDrawer}
                                    canShowDot={!isOnDeliveryPage}
                                />
                            </div>
                        }
                        {
                            (showMerchant && !showSearchInput) &&
                            <Link href={'/'} onClick={() => scrollTo({ top: 0, behavior: 'smooth' })} scroll={false} passHref>
                                <div className={styles.merchant}>
                                    {merchantSummary && <MerchantLogo className={styles.logoArea} />}
                                    <span className={`${styles.merchantName} font-sm`}>{merchantSummary?.name}</span>
                                </div>
                            </Link>
                        }
                    </div>
                    {
                        canSearch &&
                        <div className={styles.searchArea} style={!showSearchInput ? { width: 'auto' } : {}}>
                            {
                                showSearchInput ?
                                    <div className={`${styles.contentSearch}`}>
                                        <ActionInput
                                            value={searchText}
                                            classNameContainer={styles.searchInput}
                                            onChange={({ target }) => setSearchText(target.value)}
                                            placeholder='Faça sua pesquisa...'
                                            autoFocus
                                            onKeyDown={(e) => e.key === 'Enter' && searchProducts()}
                                        />
                                        <SvgClose className={styles.closeSvg} onClick={handleCloseClick} />
                                    </div>
                                    :
                                    <div onClick={() => setShowSearchInput(true)} className={styles.searchCollapsed} data-testid="search">
                                        <SvgSearch className={`${styles.svgSearch}`} />
                                    </div>
                            }
                        </div>
                    }
                </div>
            </header>
        </>
    )
}